import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import template from './ns-adp-product.html';
import {productTranslations} from './translations.js';
import {ConfigService, RunConfigService, TranslationsUtil, dateFormatter} from '@nuskin/ns-util';
import {AdrService, CurrencyService} from '@nuskin/ns-shop';
import '@polymer/paper-tooltip/paper-tooltip.js';
import {productDataObject} from './ns-adp-product-data';

/**
 * Image Imports
 */
import tooltipIcon from '../img/icon-info-blue.svg';

/**
 * Component displays selected ADR data including: products, shipping, psv, points, tax and totals.
 * Labels can be replaced with translated Strings.
 *
 * <section>
 *  <h2>Content Guidelines</h2>
 *  <p>Here are some guidelines on how to properly use this element.</p>
 * </section>
 *
 * <h1>ADP Product Component Demo</h1>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-adp-product is-demo></ns-adp-product>
 *   </template>
 * </demo-snippet>
 */
export class NsAdpProduct extends PolymerElement {
  static get is () {
    return 'ns-adp-product';
  }

  static get template () {
    let htmlElement = document.createElement('TEMPLATE');
    htmlElement.innerHTML = template;
    return htmlElement;
  }

  static get properties () {
    return {
      /**
       * Property that holds response from ADP list data,
       * holds Total Points Balance information.
       */
      products: {
        type: Array
      },
      /**
       * Label for products property.
       */
      productsLabel: {
        type: String
      },
      /**
       * Label for Manage ADP Products button.
       */
      manageADPProductsLabel: {
        type: String
      },
      /**
       * Property that holds one time products in ADP.
       */
      oneTimeProducts: {
        type: Array
      },
      /**
       * Label for one time products.
       */
      oneTimeProductsLabel: {
        type: String
      },
      /**
       * Determines if ADP contains one time products,
       * automatically set on ready() from products response.
       */
      isOneTimeProduct: {
        type: Boolean,
        value: false
      },
      /**
       * Holds PSV Balance value.
       */
      psvBalance: {
        type: Number
      },
      /**
       * Label for PSV Balance.
       */
      psvBalanceLabel: {
        type: String
      },
      /**
       * Holds Points Balance value.
       */
      pointsBalance: {
        type: Number
      },
      /**
       * Label for Points Balance.
       */
      pointsBalanceLabel: {
        type: String
      },
      /**
       * Holds Tax value.
       */
      tax: {
        type: Number
      },
      /**
       * Label for Tax.
       */
      taxLabel: {
        type: String
      },
      /**
       * Holds shipping value.
       */
      shippingTotal: {
        type: Number
      },
      /**
       * Label for Shipping.
       */
      shippingLabel: {
        type: String
      },
      /**
       * Collection of toast message objects, shared with the parent (ns-adp-wrapper).
       * Messages in this collection are displayed by the parent.
       */
      toastMessages: {
        type: Array,
        notify: true
      },
      /**
       * Holds Total value.
       */
      total: {
        type: Number
      },
      /**
       * Label for Totals.
       */
      totalLabel: {
        type: String
      },
      /**
       * Holds Subtotal  value.
       */
      subTotal: {
        type: Number
      },
      /**
       * Label for Subtotal.
       */
      subtotalLabel: {
        type: String
      },
      /**
       * Label for Program Rules.
       */
      adpProgramRulesLabel: {
        type: String
      },
      /**
       * Language property. Is set when component is called.
       */
      language: {
        type: String
      },
      /**
       * Country property. Is set when component is called.
       */
      country: {
        type: String
      },
      /**
       * Object used to share data between ns-adp-product and ns-adp-wrapper.
       */
      productDataObject: {
        type: Object,
        notify: true,
        observer: '_productDataObjectChanged'
      },
      /**
       * Disables buttons and functionality if ADR is not editable.
       */
      isNotEditable: {
        type: Boolean,
        value: false,
        reflectToAttribute: true
      },
      /**
       * Encoded value of tooltip icon used for passing this value to the template.
       */
      tooltipIcon: {
        type: String,
        value: tooltipIcon
      },

      /**
       * Computes whether the "Redeem my points" link should be hidden or not.
       */
      isRedeemPointsHidden: {
        type: Boolean,
        computed: '_isRedeemPointsHidden(isNotEditable, pointsBalance)'
      },
      /**
      * Populates component with test data and prevents any unnecessary network calls.
       */
      isDemo: {
        type: Boolean,
        value: false
      },
      /**
       * Page upon which this component appears. Used for translations.
       */
      webPageName: {
        type: String,
        value: 'shop/adp-manage'
      },
      /**
       * Translations from the server. Triggers the translation assignment process.
       * If undefined and shouldAwaitTranslations=false, this component will fetch its own strings.
       */
      serverTranslations: {
        type: Object,
        observer: '_assignTranslations'
      },
      /**
       * If false, it will start the translation assignment process without waiting.
       * If true, it will wait for serverTranslations to be set before starting the translation assignment process.
       * If serverTranslations is defined already, the component will behave as if this property is true.
       */
      shouldAwaitTranslations: {
        type: Boolean,
        value: false
      },
      /**
       * Path for adr program rules
       */
      adpProgramRulesPath: {
        type: String,
        observer: '_adpProgramRulesPathChanged'
      },
      /**
       * pass through of the showLoader property - if set to true, will show the loading spinner
       */
      showLoader: {
        type: Boolean,
        notify: true
      },
      /**
       * Id of selected ADR
       */
      adrId: {
        type: String
      },

      /**
       * Number of Points expiring this month
       */
      expiringPoints: {
        type: Number
      },

      /**
       * Label for expiringPoints.
       */
      computedExpiringPointsLabel: {
        type: String
      },
      /**
       * Stores the selectedAdr for any child components to grab from
       */
      selectedAdr: {
        type: Object,
        notify: true
      },
      /**
       * loyaltyConfig
       * Configuration for loyalty
       */
      loyaltyConfig: {
        type: Object
      },
      /**
       * loyaltyService
       * Service for loyalty
       */
      loyaltyService: {
          type: Object
      },
      /**
       * holder for the feature flags enabled for the user 
       */
       featureFlags: {
        type: Object,
        value: {}
      },
      /**
       * computed to validate whether or not to display loyalty object
       */
      displayLoyalty: {
        type: Boolean,
        computed: '_computeDisplayLoyalty(loyaltyConfig)'
      },
      productDiscountCode: {
        type: String,
        value: ''
      },
      productDiscount: {
        type: Number,
        value: 0
      },
      couponCodeSummaryTextComputed: {
        type: String,
        computed: '_computeCouponCodeSummaryText(couponCodeSummaryText, productDiscountCode, productDiscount)'
      },
      hasProductDiscountCode: {
        type: Boolean,
        computed: '_computeHasProductDiscountCode(couponCodeSummaryText, productDiscountCode)'
      },
      lineItemTotal: {
        type: Number,
        computed: '_computelineItemTotal(subTotal, productDiscount)'
      },
      computedCouponTotalLabel: {
        type: String,
        computed: '_computeCouponTotalLabel(couponTotalLabel, productDiscountCode)'
      },
      hasProductDiscountTotal: {
        type: Boolean,
        computed: '_computeHasProductDiscountCode(couponTotalLabel, productDiscountCode)'
      },
      negativeProductDiscount: {
        type: Number,
        computed: '_computenegativeProductDiscount(productDiscount)'
      }
    };
  }

  /**
   * Polymer lifecycle method.
   * This component receives data from ADP list component,
   * sets translations labels and displays these.
   */
  ready () {
    super.ready();
    if (!this.TRANSLATION_HELPER) this._localize();

    this.marketConfig = ConfigService.getMarketConfig();
    this.enablePayWithPoints = this.marketConfig.adpManage.enablePayWithPoints;
    this.enableGiftReceipt = this.marketConfig.adpManage.showSuppressPricing;
    this.useShippingCountryAdrPoints = this.marketConfig.adpManage.useShippingCountryAdrPoints;

    if (this.isDemo) {
      this._setProductData(productDataObject);
    }

    this.AdrService = AdrService;
  }

  /**
   * Sets language and country properties and prepares translations
   */
  _localize () {
    this.TRANSLATION_HELPER = new TranslationsUtil(this.language, this.country, productTranslations);
    this.language = this.TRANSLATION_HELPER.language;
    this.country = this.TRANSLATION_HELPER.country;
    if (!this.shouldAwaitTranslations) this._assignTranslations();
  }

  /**
   * Observer on serverTranslations that gathers all translations and maps them to properties.
   * Any strings set as properties and defined in the html attributes take precedence over translations from the server,
   * which in turn take precedence over the default translations in the translations file.
   */
  async _assignTranslations () {
    if (!this.TRANSLATION_HELPER) this._localize();
    this.serverTranslations = this.serverTranslations || await this.TRANSLATION_HELPER.getServerTranslations(this.webPageName);
    let allTranslations = Object.assign({}, this.TRANSLATION_HELPER.getDefaultTranslations(), this.serverTranslations);
    Object.entries(allTranslations).forEach(([key, value]) => {
      this[key] = this[key] || value;
    });

    this._setExpiringPointsLabel();
  }

  /**
   * Observer when productDataObject response is changed.
   */
  _productDataObjectChanged (data) {
    let products = data.products;
    let oneTimeProducts = data.oneTimeProducts;
    if (products !== null || oneTimeProducts !== null) {
      this._setProductData(data);
    }
  }

  /**
   * Sets product data to be displayed.
   */
  _setProductData (data) {
    this.shippingTotal = data.shippingTotal;
    this.shipping = data.shipping;
    this.psvBalance = data.psv;
    this.tax = data.tax;
    this.total = data.grandTotal;
    this.subTotal = data.itemSubTotal;
    this.pointsBalance = data.pointsBalance;
    this.expiringPoints = data.expiringPoints;
    this.products = data.products;
    this.oneTimeProducts = data.oneTimeProducts;
    this.isOneTimeProduct = data.oneTimeProducts.length > 0;
    this._setExpiringPointsLabel();
  }

  /**
   * Checks for one time products and toggles a property to display them.
   */
  _checkOneTimeProducts () {
    this.isOneTimeProduct = this.oneTimeProducts.length > 0;
  }

  /**
   * Sends user to adr edit page.
   */
  _goToAdpCart () {
    if (this.isDemo) {
      return;
    }
    let _configureExtension = pagePath => {
      return pagePath.endsWith('.html') ? pagePath : `${pagePath}.html`
    };
    let _configureBaseURL = page => {
      return `${RunConfigService.getBaseUrl()}/${RunConfigService.getRunConfig().language}_${RunConfigService.getRunConfig().country}${page}`;
    };
    let defaultEditPage = '/shop/adp-edit.html'
    // this configuration is loaded in to the component from tdc_adpEditPath on the adp-manage page in AEM
    let adpEditPath = this.adpEditPath ? _configureExtension(this.adpEditPath) : _configureBaseURL(defaultEditPage);
    window.location =  adpEditPath;
  }

  /**
   * On-click for the redeem my points link. Sets redeem points checked in local storage so that when the cart loads,
   * the redeem points checkbox will be checked.
   */
  _redeemPointsHandleClick () {
    if (this.isDemo) {
      return;
    }
    AdrService.setRedeemPointsChecked(true);
    this._goToAdpCart();
  }

  /**
   * Determines whether the redeem points link should be hidden or not. This method computes the property
   * this.isRedeemPointsHidden.
   * @param {boolean} pointsBalance this.pointsBalance, the number of points that can be redeem with this order
   * @param {boolean} isNotEditable this.isNotEditable
   * @return {boolean} true if the link should be hidden, false if not.
   */
  _isRedeemPointsHidden (isNotEditable, pointsBalance) {
    return isNotEditable || !pointsBalance;
  }

  /**
   * takes a number and returns a string representation with 2 decimal places
   * @param num
   * @returns {*|string}
   */
  _toFixed(num, decimalPlaces) {
    return num.toFixed(decimalPlaces);
  }

  /**
   * @return {boolean} Whether or not the points balance should be displayed.
   */
  _showPointsBalance (enablePayWithPoints, pointsBalance) {
    return enablePayWithPoints && pointsBalance;
  }

  _adpProgramRulesPathChanged () {
    if (this.adpProgramRulesPath && !/\.html$/.test(this.adpProgramRulesPath) && !/\.pdf$/.test(this.adpProgramRulesPath)) this.adpProgramRulesPath += '.html';
  }

  /**
   * Observer for deactivate checkbox in deactivateExtraScanCardsModal.
   * sets scan card request body action to deactivate.
   */
  _onEnableGiftReceiptChecked (e) {
    let adrId = this.adrId
    this.showLoader = true;
    this.AdrService.updateGiftCertificate(adrId, e.target.checked).finally(() => {
      this.showLoader = false;
    })
  }

  /**
   * Build the expiringPointsLabel ensures we have both translations and expiringPoints.
   */
  _setExpiringPointsLabel() {
    if(this.expiringPoints !== undefined && this.expiringPointsLabel !== undefined) {
      let runConfig = RunConfigService.getRunConfig(),
          local = `${runConfig.language}_${runConfig.country}`;

      let date = new Date();
      // I intentionally get next month so I can get 1 less day to the last of the month
      date.setFullYear(date.getFullYear(), date.getMonth() +1, 0);
      let formattedDate = dateFormatter.format(date, local);

      this.computedExpiringPointsLabel = this.expiringPointsLabel.replace(/{points}/, this.expiringPoints)
          .replace(/{date}/, formattedDate);
    }
  }

  _computeDisplayLoyalty(loyaltyConfig) {
    return loyaltyConfig && loyaltyConfig.enabled && loyaltyConfig.enabledSubscription
  }

  _computeCouponCodeSummaryText(text, code, discount) {
    return (text || '')
      .replace(/{code}/g, code)
      .replace(/{discount}/g, CurrencyService.format(discount || 0))
  }

  _computeHasProductDiscountCode(text, code) {
    return !!text && !!code;
  }

  _computelineItemTotal(subTotal, productDiscount) {
    return subTotal + (productDiscount || 0);
  }

  _computeCouponTotalLabel(label, code) {
    return (label || '')
      .replace(/{code}/g, code)
  }

  _computenegativeProductDiscount(productDiscount) {
    return (productDiscount || 0) * -1
  }
}

customElements.define(NsAdpProduct.is, NsAdpProduct);
